<script setup lang="ts">
import esriConfig from '@arcgis/core/config.js'
import { onMounted, onUnmounted, computed, nextTick, watch, ref } from 'vue'
import { useArcGISMap } from '@/components/map/composable/arcgisMapComposable'
import '@arcgis/core/assets/esri/themes/light/main.css'
import {
  handleResetView,
  handleComparisonMode,
  updateLayerLeftToolPosition,
  switchToSingleMode,
  loading,
  loadingMessage,
  isOperationAborted,
  setupPrintService,
  setupGeneralToolsPosition,
  filterLayerInfosByName,
  hasSwipeViewWithLayers,
  shouldUpdateComparison,
  shouldSwitchToSingleMode,
  handleComparisonLayerUpdate
} from '@/components/map/utils/arcgisUtils'
import { storeToRefs } from 'pinia'
import { useScenarioStore } from '@/stores/scenario'
import type { arcgisMap, arcgisSwipe, arcgisExpand } from '@/components/map/type/arcgis.type'

const scenarionStore = useScenarioStore()
const { currentItem: currentScenarioItem } = storeToRefs(scenarionStore)

const addPassiveEventListeners = () => {
  const original = EventTarget.prototype.addEventListener
  const passiveEvents = new Set(['scroll'])

  EventTarget.prototype.addEventListener = function (type, listener, options) {
    const needsPassive = passiveEvents.has(type)
    if (needsPassive) {
      options =
        typeof options === 'boolean'
          ? { capture: options, passive: true }
          : { ...options, passive: true }
    }
    return original.call(this, type, listener, options)
  }
}

const props = defineProps({
  layerUrls: {
    type: Array as () => string[],
    required: true
  },
  baseName: {
    type: String,
    required: false,
    default: 'Simulation 01'
  },
  comparisonLayerUrls: {
    type: Array as () => string[],
    required: false,
    default: () => []
  },
  comparisonBaseName: {
    type: String,
    required: false,
    default: 'Simulation 02'
  },
  baseMapId: {
    type: Number,
    required: false,
    default: null
  },
  arcgisLocationPlatformToken: {
    type: String,
    required: false,
    default: ''
  }
})

const layerUrls = computed(() => props.layerUrls)
const comparisonLayerUrls = computed(() => props.comparisonLayerUrls)
const baseName = computed(() => props.baseName)
const comparisonBaseName = computed(() => props.comparisonBaseName)
const baseMapId = computed(() => props.baseMapId)
let arcgisViewReadyHandler: any
const arcgisMapRef = ref<arcgisMap | null>(null)
const arcgisSwipeRef = ref<arcgisSwipe | null>(null)

const basemap = computed(() => {
  if (props.arcgisLocationPlatformToken) {
    return 'open/osm-style'
  } else {
    return 'streets-navigation-vector'
  }
})

const abortController = ref<AbortController | null>(null)
const isMounted = ref(false)
const pendingOperations = ref<Set<Promise<unknown>>>(new Set())

const safeAsync = async <T,>(
  operation: () => Promise<T>,
  operationName = 'unknown'
): Promise<T | null> => {
  if (!isMounted.value || abortController.value?.signal.aborted) {
    return null
  }

  const promise = executeOperation(operation, operationName)
  pendingOperations.value.add(promise)

  try {
    return await promise
  } finally {
    pendingOperations.value.delete(promise)
  }
}

const executeOperation = async <T,>(
  operation: () => Promise<T>,
  operationName: string
): Promise<T | null> => {
  if (abortController.value?.signal.aborted) {
    return null
  }

  try {
    const result = await operation()

    if (abortController.value?.signal.aborted || !isMounted.value) {
      return null
    }

    return result
  } catch (error) {
    handleOperationError(error, operationName)
    return null
  }
}

const handleOperationError = (error: any, operationName: string) => {
  const isAborted = isOperationAborted(abortController.value, isMounted.value, error)

  if (!isAborted) {
    console.error(`Operation ${operationName} failed:`, error)
  }
}

const handleViewReady = async (mapView: arcgisMap) => {
  if (!isMounted.value) return

  try {
    await nextTick()

    if (!isMounted.value) return

    setupGeneralToolsPosition(generalTools.value, isMounted.value)
    await handleResetView(mapView, resetViewTool.value)

    if (!isMounted.value || !mapView?.map) {
      console.error('mapView or map is not initialized.')
      loading.value = false
      return
    }

    const featureLayers = await createFeatureLayers(
      layerUrls.value,
      baseName.value,
      currentScenarioItem.value?.calculation_method ?? '',
      abortController.value?.signal
    )

    if (!isMounted.value || !featureLayers) return

    await addFeatureLayerToMap(mapView, featureLayers, baseName.value)
    await nextTick()

    if (!isMounted.value) return

    if (leftLegendTool.value) {
      leftLegendTool.value.layerInfos = filterLayerInfosByName(featureLayers, baseName.value)
    }
    loading.value = false
  } catch (error) {
    console.error('Error in handleViewReady:', error)
    loading.value = false
  }
}

const createViewReadyHandler = () => {
  return async (event: any) => {
    loading.value = true
    const mapView = event.detail ?? arcgisMapRef.value
    await safeAsync(() => handleViewReady(mapView), 'arcgisViewReady')
  }
}

const setupArcGISViewListener = () => {
  if (!arcgisMapRef.value || !isMounted.value) {
    console.error('arcgisMapRef is not initialized.')
    loading.value = false
    return
  }

  arcgisViewReadyHandler = createViewReadyHandler()
  if (arcgisMapRef.value && typeof arcgisMapRef.value.addEventListener === 'function') {
    arcgisMapRef.value.addEventListener('arcgisViewReadyChange', arcgisViewReadyHandler)
  }
}

const {
  layerLeftTool,
  leftLegendTool,
  layerLeftList,
  generalTools,
  rightLegendTool,
  resetViewTool,
  printTool,
  setupInterceptors,
  createFeatureLayers,
  addLayersToMap,
  addFeatureLayerToMap,
  addLayersToSwipeView,
  getLayersFromGroup,
  exportLegendBreaks,
  applyLegendBreaksToLayers,
  removeGroupLayer
} = useArcGISMap()

const initializeMap = async () => {
  setupInterceptors()
  esriConfig.apiKey = props.arcgisLocationPlatformToken || ''
  setupPrintService(printTool.value, baseMapId.value, isMounted.value)
  setupArcGISViewListener()
}

onMounted(async () => {
  addPassiveEventListeners()
  abortController.value = new AbortController()
  isMounted.value = true

  abortController.value.signal.addEventListener('abort', () => {
    loading.value = false
  })

  await safeAsync(() => initializeMap(), 'onMounted')
})

const handleComparisonModeUpdate = async (
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  currentSwipeView: any,
  newLayerUrls: string[],
  newBaseName: string,
  oldBaseName: string
) => {
  setupInterceptors()

  await handleComparisonMode(
    mapView,
    swipeView,
    newLayerUrls,
    newBaseName,
    oldBaseName,
    removeGroupLayer,
    (urls: string[], name: string, method: string) =>
      createFeatureLayers(urls, name, method, abortController.value?.signal),
    addFeatureLayerToMap,
    addLayersToSwipeView,
    exportLegendBreaks,
    applyLegendBreaksToLayers,
    rightLegendTool.value,
    leftLegendTool.value,
    baseName.value,
    currentScenarioItem.value?.calculation_method ?? ''
  )
}

const handleComparisonLayerUpdateMode = async (
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  newLayerUrls: string[],
  newBaseName: string
) => {
  setupInterceptors()

  await handleComparisonLayerUpdate(
    mapView,
    swipeView,
    baseName.value,
    newLayerUrls,
    newBaseName,
    currentScenarioItem.value?.calculation_method ?? '',
    getLayersFromGroup,
    exportLegendBreaks,
    createFeatureLayers,
    applyLegendBreaksToLayers,
    addLayersToMap,
    rightLegendTool.value,
    abortController.value?.signal,
    isMounted.value
  )
}

const handleSingleModeSwitch = async (mapView: arcgisMap, layerLeftToolView: arcgisExpand) => {
  setupInterceptors()

  await switchToSingleMode(
    mapView,
    layerLeftToolView,
    (urls: string[], name: string, method: string) =>
      createFeatureLayers(urls, name, method, abortController.value?.signal),
    addLayersToMap,
    layerUrls.value,
    baseName.value,
    leftLegendTool.value,
    currentScenarioItem.value?.calculation_method ?? ''
  )
}

const processComparisonLayerChanges = async (
  mapView: arcgisMap,
  swipeView: arcgisSwipe,
  layerLeftToolView: arcgisExpand,
  currentSwipeView: any,
  newLayerUrls: string[],
  newBaseName: string,
  oldLayerUrls: string[],
  oldBaseName: string
) => {
  if (swipeView && isMounted.value) {
    updateLayerLeftToolPosition(layerLeftToolView)
  }

  if (shouldUpdateComparison(swipeView, newLayerUrls, oldLayerUrls, isMounted.value)) {
    if (currentSwipeView) {
      await handleComparisonModeUpdate(
        mapView,
        swipeView,
        currentSwipeView,
        newLayerUrls,
        newBaseName,
        oldBaseName
      )
    } else {
      await handleComparisonLayerUpdateMode(mapView, swipeView, newLayerUrls, newBaseName)
    }
  }

  if (shouldSwitchToSingleMode(mapView, swipeView, isMounted.value)) {
    await handleSingleModeSwitch(mapView, layerLeftToolView)
  }
}

watch(
  [comparisonLayerUrls, comparisonBaseName],
  async ([newLayerUrls, newBaseName], [oldLayerUrls, oldBaseName]) => {
    await safeAsync(async () => {
      await nextTick()

      if (!arcgisMapRef.value || !isMounted.value) return

      loading.value = true
      const mapView = arcgisMapRef.value as arcgisMap
      const swipeView = arcgisSwipeRef.value as arcgisSwipe
      const layerLeftToolView = layerLeftTool.value as arcgisExpand
      const currentSwipeView = hasSwipeViewWithLayers(swipeView) ? swipeView : null

      await processComparisonLayerChanges(
        mapView,
        swipeView,
        layerLeftToolView,
        currentSwipeView,
        newLayerUrls,
        newBaseName,
        oldLayerUrls,
        oldBaseName
      )
    }, 'watchComparisonLayers')
  }
)

onUnmounted(async () => {
  isMounted.value = false

  if (abortController.value) {
    abortController.value.abort()
  }

  if (arcgisMapRef.value !== undefined && arcgisMapRef.value !== null && arcgisViewReadyHandler) {
    try {
      arcgisMapRef.value.removeEventListener?.('arcgisViewReadyChange', arcgisViewReadyHandler)
    } catch (error) {
      console.warn('Error removing event listener:', error)
    }
    arcgisViewReadyHandler = null
  }

  const destroyComponent = (component: any, name: string) => {
    try {
      component?.destroy()
    } catch (error) {
      console.warn(`Error destroying ${name}:`, error)
    }
  }

  destroyComponent(arcgisSwipeRef.value, 'Swipe component')
  destroyComponent(arcgisMapRef.value, 'Map component')
  destroyComponent(printTool.value, 'Print tool')

  loadingMessage.value = 'Loading map, please wait...'
  loading.value = false

  if (pendingOperations.value.size > 0) {
    await Promise.allSettled(Array.from(pendingOperations.value))
  }
})
</script>

<template>
  <div class="app-container">
    <v-overlay :model-value="loading" class="map-loading-overlay" persistent>
      <div class="custom-loading-box">
        <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
        <div class="loading-message">{{ loadingMessage }}</div>
      </div>
    </v-overlay>
    <arcgis-map ref="arcgisMapRef" zoom="15" :basemap="basemap" :animationsDisabled="true">
      <arcgis-expand slot="top-right" ref="layerLeftTool" group="leftTools">
        <arcgis-layer-list ref="layerLeftList"></arcgis-layer-list>
      </arcgis-expand>
      <arcgis-expand slot="bottom-left" group="tools" expanded="true">
        <arcgis-legend
          ref="leftLegendTool"
          legendStyle="card"
          hideLayersNotInCurrentView="true"
        ></arcgis-legend>
      </arcgis-expand>
      <arcgis-placement ref="generalTools" position="manual">
        <arcgis-fullscreen></arcgis-fullscreen>
        <arcgis-home ref="resetViewTool"></arcgis-home>
        <arcgis-zoom></arcgis-zoom>
        <arcgis-expand mode="auto">
          <arcgis-basemap-gallery></arcgis-basemap-gallery>
        </arcgis-expand>
        <arcgis-expand>
          <arcgis-print ref="printTool"></arcgis-print>
        </arcgis-expand>
      </arcgis-placement>
      <arcgis-swipe
        v-if="comparisonLayerUrls.length > 0"
        ref="arcgisSwipeRef"
        direction="horizontal"
        swipe-position="50"
      ></arcgis-swipe>
      <arcgis-expand v-if="comparisonLayerUrls.length > 0" slot="bottom-right" expanded="true">
        <arcgis-legend
          v-if="comparisonLayerUrls.length > 0"
          ref="rightLegendTool"
          legendStyle="card"
          hideLayersNotInCurrentView="true"
        ></arcgis-legend>
      </arcgis-expand>
      <div v-if="comparisonLayerUrls.length > 0" class="swipe-label top-left-label">
        {{ baseName }}
      </div>
      <div v-if="comparisonLayerUrls.length > 0" class="swipe-label top-right-label">
        {{ comparisonBaseName }}
      </div>
    </arcgis-map>
  </div>
</template>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.app-container > .arcgis-map {
  height: 100%;
  position: relative;
}

.swipe-label {
  position: absolute;
  top: 10px;
  font-size: 16px;
  color: black;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  pointer-events: none;
  z-index: 10;
}

.top-left-label {
  left: 10px;
}

.top-right-label {
  right: 10px;
}

.layers-expander {
  left: 150px !important;
  top: 100px !important;
}
.map-loading-overlay {
  z-index: 1000;
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-loading-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.15);
  padding: 32px 40px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 240px;
}

.loading-message {
  margin-top: 18px;
  font-size: 1.1rem;
  color: #333;
  text-align: center;
  word-break: break-word;
}
</style>
